'use client';

import { useMemo } from 'react';

import { TIMELINE_MAX_THUMBNAILS, VERTICAL_OVERRIDE_THUMBNAIL_COUNT } from './constants';
import { type ThumbnailMetadata, type VideoMetadata } from './types';
import { calculateThumbnails, calculateThumbnailTimes, getEffectiveThumbnailsPerSecond } from './videoUtils';

interface UseVideoThumbnailsProps {
    videoMetadata: VideoMetadata | null | undefined;
    stripHeight: number;
    containerWidth: number;
    thumbnailsPerSecond?: number;
}

interface UseVideoThumbnailsReturn {
    thumbnails: ThumbnailMetadata[];
    /** Frame timestamps, stable across container resizes. */
    times: number[];
    isReady: boolean;
}

export function useVideoThumbnails({
    videoMetadata,
    stripHeight,
    containerWidth,
    thumbnailsPerSecond = 0.5,
}: UseVideoThumbnailsProps): UseVideoThumbnailsReturn {
    const { duration = 0, isVertical = false } = videoMetadata ?? {};

    const effectiveThumbnailsPerSecond = useMemo(() => {
        if (duration <= 0) return 0;

        return getEffectiveThumbnailsPerSecond({
            duration,
            thumbnailsPerSecond: isVertical ? VERTICAL_OVERRIDE_THUMBNAIL_COUNT : thumbnailsPerSecond,
            maxThumbnails: TIMELINE_MAX_THUMBNAILS,
            thumbnailCountOverride: isVertical ? VERTICAL_OVERRIDE_THUMBNAIL_COUNT : null,
        });
    }, [duration, thumbnailsPerSecond, isVertical]);

    const times = useMemo(
        () => calculateThumbnailTimes(duration, effectiveThumbnailsPerSecond),
        [duration, effectiveThumbnailsPerSecond],
    );

    const thumbnails = useMemo(() => {
        if (effectiveThumbnailsPerSecond <= 0 || stripHeight <= 0 || containerWidth <= 0) {
            return [];
        }
        return calculateThumbnails({
            duration,
            stripHeight,
            thumbnailsPerSecond: effectiveThumbnailsPerSecond,
            containerWidth,
            isVertical,
        });
    }, [duration, stripHeight, effectiveThumbnailsPerSecond, containerWidth, isVertical]);

    return {
        thumbnails,
        times,
        isReady: Boolean(videoMetadata && thumbnails.length > 0),
    };
}
